import api from './api';

// ==================== 类型定义 ====================

export interface ReferralWallet {
  total_earned_cents: number;
  pending_cents: number;
  available_cents: number;
  frozen_cents: number;
  withdrawn_cents: number;
}

export interface ReferralOverview {
  invite_code: string;
  commission_rate: number;
  total_invites: number;
  wallet: ReferralWallet;
}

export interface InviteItem {
  username_masked: string;
  registered_at: string;
  has_paid: boolean;
  total_commission_cents: number;
}

export interface CommissionItem {
  id: number;
  referee_username_masked: string;
  order_amount_cents: number;
  commission_rate: number;
  commission_amount_cents: number;
  currency: string;
  status: string;
  settled_at: string | null;
  created_at: string;
}

export interface WithdrawalItem {
  id: number;
  amount_cents: number;
  currency: string;
  payment_method: string;
  wallet_address: string;
  status: string;
  reject_reason: string;
  tx_hash: string;
  created_at: string;
  reviewed_at: string | null;
  completed_at: string | null;
}

export interface PaginatedResponse<T> {
  total: number;
  list: T[];
}

// ==================== 管理端类型 ====================

export interface AdminReferralOverview {
  total_referrals: number;
  total_commissions: number;
  pending_withdrawals: number;
}

export interface AdminReferralItem {
  id: number;
  referrer_client_id: number;
  referrer_username: string;
  referee_client_id: number;
  referee_username: string;
  invite_code: string;
  status: string;
  created_at: string;
}

export interface AdminCommissionItem {
  id: number;
  referrer_client_id: number;
  referrer_username: string;
  referee_client_id: number;
  referee_username: string;
  order_id: string;
  order_amount_cents: number;
  commission_rate: number;
  commission_amount_cents: number;
  currency: string;
  status: string;
  settled_at: string | null;
  created_at: string;
}

export interface AdminWithdrawalItem {
  id: number;
  client_id: number;
  username: string;
  amount_cents: number;
  currency: string;
  payment_method: string;
  wallet_address: string;
  status: string;
  reviewed_by: number | null;
  reviewed_at: string | null;
  reject_reason: string;
  completed_at: string | null;
  tx_hash: string;
  created_at: string;
}

// ==================== 客户端 API ====================

class ReferralService {
  async getOverview(): Promise<ReferralOverview> {
    const response = await api.post('/api/referral/overview');
    return response.data.data;
  }

  async getInvites(page = 1, pageSize = 20): Promise<PaginatedResponse<InviteItem>> {
    const response = await api.post('/api/referral/invites', { page, page_size: pageSize });
    return response.data.data;
  }

  async getCommissions(status = '', page = 1, pageSize = 20): Promise<PaginatedResponse<CommissionItem>> {
    const response = await api.post('/api/referral/commissions', { status, page, page_size: pageSize });
    return response.data.data;
  }

  async getWithdrawals(page = 1, pageSize = 20): Promise<PaginatedResponse<WithdrawalItem>> {
    const response = await api.post('/api/referral/withdrawals', { page, page_size: pageSize });
    return response.data.data;
  }

  async requestWithdrawal(amountCents: number, walletAddress: string): Promise<{ withdrawal_id: number }> {
    const response = await api.post('/api/referral/withdraw', { amount_cents: amountCents, wallet_address: walletAddress });
    return response.data.data;
  }

  // ==================== 管理端 API ====================

  async getAdminOverview(): Promise<AdminReferralOverview> {
    const response = await api.post('/api/admin/referral/overview');
    return response.data.data;
  }

  async getAdminReferrals(referrerClientId = 0, page = 1, pageSize = 20): Promise<PaginatedResponse<AdminReferralItem>> {
    const response = await api.post('/api/admin/referral/referrals', { referrer_client_id: referrerClientId, page, page_size: pageSize });
    return response.data.data;
  }

  async getAdminCommissions(referrerClientId = 0, status = '', page = 1, pageSize = 20): Promise<PaginatedResponse<AdminCommissionItem>> {
    const response = await api.post('/api/admin/referral/commissions', { referrer_client_id: referrerClientId, status, page, page_size: pageSize });
    return response.data.data;
  }

  async getAdminWithdrawals(status = '', page = 1, pageSize = 20): Promise<PaginatedResponse<AdminWithdrawalItem>> {
    const response = await api.post('/api/admin/referral/withdrawals', { status, page, page_size: pageSize });
    return response.data.data;
  }

  async reviewWithdrawal(withdrawalId: number, action: 'approve' | 'reject', rejectReason = ''): Promise<void> {
    await api.post('/api/admin/referral/withdrawal/review', { withdrawal_id: withdrawalId, action, reject_reason: rejectReason });
  }

  async completeWithdrawal(withdrawalId: number, txHash: string): Promise<void> {
    await api.post('/api/admin/referral/withdrawal/complete', { withdrawal_id: withdrawalId, tx_hash: txHash });
  }
}

export const referralService = new ReferralService();
export default referralService;
